<template>
  <div v-if="!!phaseData">
    <div>
      <field-label label="Resultado dos testes" titleClass="text-h6" titleColorClass="grey--text text--darken-2">
        <div
          class="mt-4 d-flex flex-row"
          :class="{ 'flex-column gap-8': $vuetify.breakpoint.smAndDown, 'gap-12': !$vuetify.breakpoint.smAndDown }"
        >
          <field-label v-for="indicator in indicators" :key="indicator.key" :label="indicator.name">
            <div class="d-flex gap-2 mt-2 align-center">
              <text-field
                :min="0"
                type="number"
                v-if="phaseData.resultado_manual"
                v-model="phaseData[indicator.key]"
                :max="indicator.max"
              />
              <span
                v-else
                :class="{
                  'red--text': phaseData[indicator.key] > 9,
                  'black--text text-h6': phaseData[indicator.key] == null,
                  'big-number': phaseData[indicator.key] != null,
                }"
                >{{ phaseData[indicator.key] == null ? 'Indefinido' : phaseData[indicator.key] }}</span
              >
              <span
                v-if="phaseData[indicator.key]"
                class="ml-2"
                :class="{ 'red--text': phaseData[indicator.key] > 9 }"
                style="width: 120px"
                >{{ phaseData[indicator.key] > 9 ? 'Moderado a grave' : 'Leve' }}</span
              >
            </div>
          </field-label>
        </div>

        <v-switch
          class="my-4"
          label="Aplicar resultado manualmente"
          v-model="phaseData.resultado_manual"
          hide-details
        />

        <area-slider
          v-if="!phaseData.resultado_manual"
          :data="phaseData"
          :area="areas"
          :types="skinProblemTypes"
          :max="4"
        />

        <field-label class="mt-8" label="Anotações">
          <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
        </field-label>
      </field-label>
    </div>
    <reference-image-dialog ref="referenceImageDialog" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import TextArea from '@/components/TextArea.vue'
import TextField from '@/components/TextField.vue'
import ReferenceImageDialog from '../../ReferenceImageDialog.vue'
import AreaSlider from './AreaSlider.vue'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

export default {
  extends: BasePhase,
  components: { FieldLabel, TextArea, TextField, ReferenceImageDialog, AreaSlider },
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey']),
    computedPasi() {
      if (!this.phaseData || this.phaseData.resultado_manual) {
        return this.phaseData?.pasi || 0
      }

      let result = 0
      for (let areaObject of this.areas) {
        let area = this.phaseData[areaObject.key]
        let affectedAreaWeight = this.getAffectedAreaWeight(area.area)
        result += (area.eritema + area.descamacao + area.infiltracao) * areaObject.factor * affectedAreaWeight
      }
      return (Math.round(100 * result) / 100).toFixed(2)
    },
    computedBsa() {
      if (!this.phaseData || this.phaseData.resultado_manual) {
        return this.phaseData?.bsa || 0
      }

      let result = 0
      for (let areaObject of this.areas) {
        let area = this.phaseData[areaObject.key]
        result += area.area * areaObject.factor
      }
      return (Math.round(100 * result) / 100).toFixed(2)
    },
  },
  data() {
    return {
      skinProblemTypes: [
        { key: 'eritema', name: 'Eritema' },
        { key: 'descamacao', name: 'Descamação' },
        { key: 'infiltracao', name: 'Infiltração' },
      ],
      areas: [
        {
          key: 'cabeca_pescoco',
          name: 'CABEÇA E PESCOÇO',
          factor: 0.1,
        },
        {
          key: 'tronco',
          name: 'TRONCO',
          factor: 0.3,
        },
        {
          key: 'membros_superiores',
          name: 'MEMBROS SUPERIORES	',
          factor: 0.2,
        },
        {
          key: 'membros_inferiores',
          name: 'MEMBROS INFERIORES',
          factor: 0.4,
        },
      ],
      indicators: [
        {
          key: 'pasi',
          name: 'PASI',
          max: 72,
        },
        {
          key: 'bsa',
          name: 'BSA',
          max: 100,
        },
      ],
    }
  },
  mounted() {
    if (this.predominantSymptom !== this.phaseData.predominantSymptom) {
      this.phaseData = this.getDefaultData()
      this.phaseData.predominantSymptom = this.predominantSymptom
    }
  },
  methods: {
    getAffectedAreaWeight(value) {
      if (value === 0) {
        return 0
      } else if (value < 10) {
        return 1
      } else if (value >= 10 && value < 30) {
        return 2
      } else if (value >= 30 && value < 50) {
        return 3
      } else if (value >= 50 && value < 70) {
        return 4
      } else if (value >= 70 && value < 90) {
        return 5
      } else {
        return 6
      }
    },
    showImage(imageSrc, description, title) {
      this.$refs.referenceImageDialog.openDialog(imageSrc, title, description)
    },
    getDefaultData() {
      const data = {
        pasi: null,
        bsa: null,
        resultado_manual: false,
        cabeca_pescoco: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
        tronco: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
        membros_superiores: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
        membros_inferiores: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
        notes: null,
      }
      return data
    },
  },
  watch: {
    computedPasi(newVal) {
      if (this.phaseData.resultado_manual) {
        return
      }
      this.phaseData.pasi = Number(newVal).toFixed(2)
    },
    computedBsa(newVal) {
      if (this.phaseData.resultado_manual) {
        return
      }
      this.phaseData.bsa = Number(newVal).toFixed(2)
    },
  },
}
</script>

<style scoped>
.big-number {
  font-weight: medium;
  color: green;
  font-size: 2.5rem;
}
.area-box {
  border: 1px solid grey;
  border-radius: 10px;
}
.slider-image {
  border: 4px solid #1976d2;
}
.area-component.small :deep(.col) {
  flex-basis: auto !important;
}
</style>
